import StopCodingDrawer from "../components/StopCoding/StopCodingDrawer";
import { GetStopsByMachineId, GetStopsByMwc, GetStopsByWorkcenter, Random1000 } from "../services/StopCodingService";
import { StopCause } from "../Types/StopCause";
import { useEffect, useLayoutEffect, useState, useCallback, useRef } from "react";
import { useAppDispatch } from "../app/hooks";
import { Button, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, Spinner } from "@fluentui/react-components";
import { ArrowClockwise20Regular, Navigation20Regular } from "@fluentui/react-icons";
import { Modal } from "../components/Modal";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { getFilenameFromContentDisposition } from "../utils/httpFileMeta";
import { useStopCodingDispatch, useStopCodingSelector } from "../components/OI/redux/ReduxContext";
import StopCodingDrillDown from "../components/StopCoding/StopCodingDrilldown";
import StopCodingCascade from "../components/StopCoding/StopCodingCascade";
import StopCodingTableList from "../components/StopCoding/StopCodingTableList";
import { clearFilter } from "../Slices/StopCodingSlice";

const STOP_CODING_FILTER_STORAGE_KEY = 'stopCodingFilterValue';
const STOP_CODING_FILTER_DISPLAY_STORAGE_KEY = 'stopCodingFilterDisplayValue';
const STOP_CODING_FILTER_TYPE_STORAGE_KEY = 'stopCodingFilterType';
const STOP_CAUSE_CACHE_LIMIT = 10;
// Caps memory when large selections are cached; the most recent entry is always kept.
const STOP_CAUSE_CACHE_MAX_STOPS = 100000;
// Older entries are loaded again behind the spinner, so the drilldown opens on current data.
const STOP_CAUSE_CACHE_MAX_AGE_MS = 30 * 60 * 1000;

type CachedStopCauses = {
    stopCauses: StopCause[];
    loadedAt: number;
};

// Stops of the most recently loaded selections, keyed by "type:value", oldest first.
// Kept at module level so it survives leaving and reopening the tab.
const stopCauseCache = new Map<string, CachedStopCauses>();

const cacheStopCauses = (cacheKey: string, stopCauses: StopCause[]) => {
    stopCauseCache.delete(cacheKey);
    stopCauseCache.set(cacheKey, { stopCauses, loadedAt: Date.now() });

    let cachedStopCount = 0;
    stopCauseCache.forEach(cached => {
        cachedStopCount += cached.stopCauses.length;
    });

    while (stopCauseCache.size > 1 && (stopCauseCache.size > STOP_CAUSE_CACHE_LIMIT || cachedStopCount > STOP_CAUSE_CACHE_MAX_STOPS)) {
        const oldestKey: string | undefined = stopCauseCache.keys().next().value;
        if (oldestKey === undefined) {
            break;
        }
        cachedStopCount -= stopCauseCache.get(oldestKey)?.stopCauses.length ?? 0;
        stopCauseCache.delete(oldestKey);
    }
};

const getFreshCachedStopCauses = (cacheKey: string): StopCause[] | undefined => {
    const cached = stopCauseCache.get(cacheKey);
    if (!cached || Date.now() - cached.loadedAt >= STOP_CAUSE_CACHE_MAX_AGE_MS) {
        return undefined;
    }

    // Mark it as most recently used, keeping its load time.
    stopCauseCache.delete(cacheKey);
    stopCauseCache.set(cacheKey, cached);
    return cached.stopCauses;
};

const sameValue = (field: keyof StopCause) => (left: StopCause, right: StopCause) => left[field] === right[field];
const sameTime = (field: 'StartTime' | 'EndTime') => (left: StopCause, right: StopCause) =>
    Object.is(left[field].getTime(), right[field].getTime());

// One comparer per field; the Record type makes a new StopCause field fail to compile until it is added here.
const stopCauseFieldComparers: Record<keyof StopCause, (left: StopCause, right: StopCause) => boolean> = {
    StartTime: sameTime('StartTime'),
    EndTime: sameTime('EndTime'),
    MachineId: sameValue('MachineId'),
    MWC: sameValue('MWC'),
    WorkCenter: sameValue('WorkCenter'),
    MachineStopReason: sameValue('MachineStopReason'),
    Alias: sameValue('Alias'),
    Duration: (left, right) => Object.is(left.Duration?.totalMilliseconds, right.Duration?.totalMilliseconds),
    StopThreshold: sameValue('StopThreshold'),
    Id: sameValue('Id'),
    Comment: sameValue('Comment'),
    HasImage: sameValue('HasImage'),
    AttachedFileIds: (left, right) => left.AttachedFileIds.join() === right.AttachedFileIds.join(),
    CatalogueCode: sameValue('CatalogueCode'),
};
const stopCauseComparers = Object.values(stopCauseFieldComparers);

const isSameStopCauses = (left: StopCause[], right: StopCause[]) =>
    left.length === right.length
    && left.every((stop, index) => stopCauseComparers.every(isSame => isSame(stop, right[index])));

export default function StopCodingDashboardNew() {


    const defaultFromDate = new Date();
    defaultFromDate.setDate(defaultFromDate.getDate() - 14);

    const [filteredStopCauses, setFilteredStopCauses] = useState<StopCause[]>([]);
    const [drillDownStopCauses, setDrillDownStopCauses] = useState<StopCause[]>([]);

    const [loading, setLoading] = useState(true);
    const [compareMode, setCompareMode] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [fromDate, setFromDate] = useState<Date | null>(defaultFromDate);
    const [toDate, setToDate] = useState<Date | null>(new Date());
    const appDispatch = useAppDispatch();

    const dispatch = useStopCodingDispatch();
    const chartMode = useStopCodingSelector(state => state.chartMode);
    const stopCodingFilterValue = useStopCodingSelector(state => state.stopCodingFilterValue);
    const stopCodingFilterDisplayValue = useStopCodingSelector(state => state.stopCodingFilterDisplayValue);
    const stopCodingFilterType = useStopCodingSelector(state => state.stopCodingFilterType);

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const clearStopCodingFilter = useCallback(() => {
        dispatch({ type: 'SET_FIELD', payload: { field: 'stopCodingFilterValue', value: '' } });
        dispatch({ type: 'SET_FIELD', payload: { field: 'stopCodingFilterDisplayValue', value: '' } });
        dispatch({ type: 'SET_FIELD', payload: { field: 'stopCodingFilterType', value: 'all' } });
        window.localStorage.removeItem(STOP_CODING_FILTER_STORAGE_KEY);
        window.localStorage.removeItem(STOP_CODING_FILTER_DISPLAY_STORAGE_KEY);
        window.localStorage.removeItem(STOP_CODING_FILTER_TYPE_STORAGE_KEY);
        appDispatch(clearFilter());
    }, [appDispatch, dispatch]);

    const hasSelection = stopCodingFilterType !== 'all' && Boolean(stopCodingFilterValue);

    const formatDateYmd = (date: Date): string => date.toLocaleDateString("sv-se");

    const formatDateYmdHm = (date: Date, hours: number, minutes: number): string => {
        const pad2 = (n: number) => String(n).padStart(2, '0');
        return `${formatDateYmd(date)}T${pad2(hours)}:${pad2(minutes)}`;
    };

    const handleGenerateExcel = async () => {
        const apiUrl = (process.env.REACT_APP_API_URL ?? '').replace(/\/+$/, '');

        if (!apiUrl) {
            console.error('REACT_APP_API_URL is not set');
            return;
        }

        if (!fromDate || !toDate) {
            console.error('From/To dates are required');
            return;
        }
        const from = formatDateYmd(fromDate);
        const to = formatDateYmd(toDate);
        const toInclusive = formatDateYmdHm(toDate, 23, 59);

        const { locale, timeZone } = Intl.DateTimeFormat().resolvedOptions();
        const deviceLocale = locale ?? navigator.language ?? "en-GB";
        const deviceTimeZone = timeZone ?? "UTC";
        const localisation = `${deviceLocale}|${deviceTimeZone}`;

        const url =
            `${apiUrl}/stopcause/analysis/excel` +
            `?from=${encodeURIComponent(from)}` +
            `&to=${encodeURIComponent(toInclusive)}` +
            `&localisation=${encodeURIComponent(localisation)}`;

        try {
            const response = await fetch(url, {
                method: 'GET',
            });

            if (!response.ok) {
                const bodyText = await response.text().catch(() => '');
                throw new Error(`Failed to generate Excel: ${response.status} ${response.statusText}${bodyText ? ` - ${bodyText}` : ''}`);
            }

            const blob = await response.blob();
            const suggestedFilename = getFilenameFromContentDisposition(response.headers.get('content-disposition'));
            const filename = suggestedFilename ?? `stopcause-analysis-${from}-${to}.xlsx`;

            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);

            handleCloseModal();
        } catch (error) {
            console.error('Error generating Excel report:', error);
        }
    };

    const loadControllerRef = useRef<AbortController | null>(null);

    const loadStopCauses = useCallback(async (forceRefresh = false) => {
        // Cancel the previous load, so its response can't overwrite this one.
        loadControllerRef.current?.abort();
        const controller = new AbortController();
        loadControllerRef.current = controller;

        const showStopCauses = (stopCauses: StopCause[]) => {
            setFilteredStopCauses(stopCauses);
            dispatch({ type: 'SET_FIELD', payload: { field: 'allStopCauses', value: stopCauses } });
        };

        if (!stopCodingFilterValue) {
            setFilteredStopCauses([]);
            setDrillDownStopCauses([]);
            dispatch({ type: 'SET_FIELD', payload: { field: 'allStopCauses', value: [] } });
            setLoading(false);
            return;
        }

        const cacheKey = `${stopCodingFilterType}:${stopCodingFilterValue}`;
        const cachedStopCauses = forceRefresh ? undefined : getFreshCachedStopCauses(cacheKey);

        if (cachedStopCauses) {
            // Show the stops from the last load right away and refresh them in the background.
            showStopCauses(cachedStopCauses);
            setLoading(false);
        } else {
            setLoading(true);
        }

        let data: StopCause[] | null = [];
        if (stopCodingFilterType === 'workcenter') {
            data = await GetStopsByWorkcenter(stopCodingFilterValue, controller.signal);
        } else if (stopCodingFilterType === 'group') {
            data = await GetStopsByMwc(stopCodingFilterValue, controller.signal);
        } else if (stopCodingFilterType === 'machine') {
            data = await GetStopsByMachineId(stopCodingFilterValue, controller.signal);
        }

        // Superseded by another selection or refresh, or the tab was closed.
        if (controller.signal.aborted) {
            return;
        }

        if (data) {
            const validData = data.filter(stop => {
                const startYear = new Date(stop.StartTime).getFullYear();
                const endYear = stop.EndTime ? new Date(stop.EndTime).getFullYear() : 0;
                return startYear <= 3000 && endYear <= 3000;
            });

            if (cachedStopCauses && isSameStopCauses(validData, cachedStopCauses)) {
                // Nothing changed: keep the shown array, so the charts and table keep their state.
                cacheStopCauses(cacheKey, cachedStopCauses);
            } else {
                cacheStopCauses(cacheKey, validData);
                showStopCauses(validData);
            }
        } else {
            // The request failed: keep the stops from the last successful load, if there is one.
            showStopCauses(stopCauseCache.get(cacheKey)?.stopCauses ?? []);
        }

        setLoading(false);
    }, [dispatch, stopCodingFilterType, stopCodingFilterValue]);

    const refreshStopCauses = () => {
        loadStopCauses(true);
    };

    // A layout effect, so cached stops are shown before the first paint instead of flashing the spinner.
    useLayoutEffect(() => {
        loadStopCauses();
        return () => loadControllerRef.current?.abort();
    }, [loadStopCauses]);

    useEffect(() => {
        const recentDaysData = filterRecentDays(filteredStopCauses, compareMode ? 14 : 7);
        setDrillDownStopCauses(recentDaysData);
    }, [compareMode, filteredStopCauses]);


    const filterRecentDays = (data: StopCause[], days: number): StopCause[] => {
        const now = new Date();
        const fromDate = new Date(now);
        fromDate.setDate(fromDate.getDate() - (days - 1));
        fromDate.setHours(0, 0, 0, 0);

        return data.filter(stop => {
            const stopDate = new Date(stop.StartTime);
            return stopDate >= fromDate && stopDate <= now;
        });
    };

    const hasChartData = filteredStopCauses.length > 0;

    const setSharedChartMode = (mode: 'count' | 'duration') => {
        dispatch({ type: 'SET_FIELD', payload: { field: 'chartMode', value: mode } });
    };

    return (
        <>
            <div >
                <div className="fixed inset-0 bg-cover bg-center bg-light dark:bg-dark -z-10 text-primary-dark dark:text-primary-light " />
                {loading && (
                    <div className="fixed inset-0  flex items-center justify-center">
                        <div className="text-center">
                            <Spinner />
                            <div>Loading stop causes...</div>
                        </div>
                    </div>
                )}

                <div className="flex w-full min-w-0 overflow-x-hidden px-0 md:pr-6 pt-0">
                    <StopCodingDrawer startOpen={true} className="fixed md:relative" />
                    <div className="flex min-w-0 flex-col flex-grow overflow-x-hidden p-4">

                        <div className="">
                            {!hasSelection ? (
                                <div className="p-4">
                                    <p>Select a workcenter, group, or machine in the asset tree to load stop charts.</p>
                                </div>
                            ) : !hasChartData ? (
                                loading ? (
                                    null
                                ) : (
                                    <div className="p-4">
                                        <p>No stop causes found for {stopCodingFilterType} {stopCodingFilterDisplayValue || stopCodingFilterValue}.</p>
                                    </div>
                                )
                            ) : (
                                <>


                                    <div className="mb-4 flex items-center justify-end lg:justify-between gap-4">
                                        <div className="hidden lg:flex items-center gap-2">

                                            {chartMode === "duration" ? (
                                                <button className="min-w-[84px] bg-oi-button-blue p-2" onClick={() => setSharedChartMode('count')}>
                                                    Count
                                                </button>
                                            ) : (
                                                <button className="min-w-[84px] bg-oi-button-blue p-2" onClick={() => setSharedChartMode('duration')}>
                                                    Duration
                                                </button>
                                            )}


                                            {compareMode ? (
                                                <button className="min-w-[160px] bg-oi-button-blue p-2" onClick={() => setCompareMode(!compareMode)}>
                                                    Disable compare
                                                </button>
                                            ) : (
                                                <button className="min-w-[160px] bg-oi-button-blue p-2" onClick={() => setCompareMode(!compareMode)}>
                                                    Enable compare
                                                </button>
                                            )}


                                            <button className="min-w-[160px] bg-oi-button-blue p-2" onClick={() => setIsModalOpen(true)}>
                                                Generate excel
                                            </button>

                                            <Button
                                                appearance="subtle"
                                                icon={<ArrowClockwise20Regular />}
                                                aria-label="Refresh stop codes"
                                                title="Refresh stop codes"
                                                onClick={refreshStopCauses}
                                            />
                                        </div>

                                        <div className="lg:hidden">
                                            <Menu>
                                                <MenuTrigger disableButtonEnhancement>
                                                    <Button
                                                        appearance="secondary"
                                                        icon={<Navigation20Regular />}
                                                        aria-label="Open actions menu"
                                                        title="Actions"
                                                    />
                                                </MenuTrigger>
                                                <MenuPopover>
                                                    <MenuList>
                                                        <MenuItem onClick={() => setSharedChartMode('count')}>Count</MenuItem>
                                                        <MenuItem onClick={() => setSharedChartMode('duration')}>Duration</MenuItem>
                                                        <MenuItem onClick={() => setCompareMode(!compareMode)}>
                                                            {compareMode ? 'Disable Compare' : 'Enable Compare'}
                                                        </MenuItem>
                                                        <MenuItem onClick={() => setIsModalOpen(true)}>Generate Excel</MenuItem>
                                                        <MenuItem onClick={refreshStopCauses}>Refresh</MenuItem>
                                                    </MenuList>
                                                </MenuPopover>
                                            </Menu>
                                        </div>
                                    </div>
                                    <StopCodingDrillDown compareMode={compareMode} />
                                    <StopCodingCascade key={`cascade-${stopCodingFilterValue || 'all'}`} />
                                    <div className="min-w-0">
                                        <StopCodingTableList />
                                    </div>
                                </>
                            )}
                        </div>

                    </div>
                </div>

                <Modal isOpen={isModalOpen}
                    onClose={handleCloseModal}
                    title="Generate Excel Report"
                    width="96">
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-col gap-2">
                            <h6>From</h6>
                            <DatePicker
                                placeholder={new Date().toLocaleDateString("sv-se")}
                                value={fromDate}
                                onSelectDate={(date) => setFromDate(date ?? null)}
                            />
                        </div>
                        <div className="flex flex-col gap-2">
                            <h6>To</h6>
                            <DatePicker
                                placeholder={new Date().toLocaleDateString("sv-se")}
                                value={toDate}
                                onSelectDate={(date) => setToDate(date ?? null)}
                            />
                        </div>
                    </div>
                    <div className="mt-8 flex justify-end space-x-5">
                        <button className=" px-2 py-1 bg-oi-button-blue" onClick={handleGenerateExcel} disabled={!fromDate || !toDate}>
                            Generate
                        </button>
                    </div>

                </Modal>
            </div>
        </>
    );
}